<template>
  <section id="wizard-step-3" class="form-content">
    <sc-form :form="formContent" :has-style="false" :rules="rules" @submit="submitNextStep">
      <template slot="fields" slot-scope="{ fields }">
        <section class="upload-form">
          <div class="upload-form__step">
            <div class="label">
              <span class="upload-form__step__label">
                <span v-if="previewMapIsEmpty()">
                  <sc-tag size="is-medium" rounded>5</sc-tag>
                  Identifique a informação de cada coluna
                  <p class="additional-label">Agora, digite coluna que representa cada informação.</p>
                  <p class="additional-label">Caso não tenha algum dos dados listados, pode deixar em branco.</p>
                </span>
                <span v-else>
                  <p class="additional-label">Confirme se os dados estão corretos para terminar de criar o modelo da fatura.</p>
                </span>
              </span>
            </div>

            <section id="billing-template-step-3-fields">
              <div class="columns">
                <div class="column column-value-config">
                  <p class="column-header">Nome do dado</p>
                </div>
                <div class="column" :class="{'column-letter-config': !previewMapIsEmpty()}">
                  <p class="column-header">Coluna</p>
                </div>
                <div class="column column-value-config" v-if="!previewMapIsEmpty()">
                  <p class="column-header">Nome do dado na fatura</p>
                </div>
              </div>

              <div v-for="[key, value] in columnsEnum">
                <div class="columns" v-if="previewMapIsEmpty() || previewMap[key]">
                  <div class="column column-value-config">
                    <template v-if="previewMap[key] && value.hasMultipleTypes">
                      <p class="column-name" v-if="fields[key].type === 'PERCENTAGE'"> {{ value.label }} (%)</p>
                      <p class="column-name" v-else> {{ value.label }} (R$)</p>
                    </template>
                    <template v-else>
                      <p class="column-name"> {{ value.label }} </p>
                    </template>
                    <sc-field v-if="value.hasMultipleTypes && previewMapIsEmpty()" :id="`radio-${value.label}`" class="tax-field">
                      <p class="column-type"> Valor em: </p>
                      <div class="percentage-field">
                        <label class="radio-label"> % </label>
                        <sc-radio
                            native-value="PERCENTAGE"
                            v-model="fields[key].type">
                        </sc-radio>
                      </div>
                      <div class="absolute-field">
                        <label class="radio-label"> R$ </label>
                        <sc-radio
                            native-value="ABSOLUTE"
                            v-model="fields[key].type">
                          </sc-radio>
                      </div>
                    </sc-field>
                  </div>
                  <div class="column" :class="{'column-letter-config': !previewMapIsEmpty()}">
                    <sc-field :id="`input-${key}`" :prop="key" class="letter-input">
                      <sc-input
                        :disabled="!previewMapIsEmpty()"
                        v-model="fields[key].letter"
                        style="width: 66px; height: 39px"
                        @input.native="$event.target.value = $event.target.value.toUpperCase().replace(/[^a-zA-Z]/g, '').substring(0, 2)"
                        placeholder="AA" />
                    </sc-field>
                  </div>
                  <div class="column" v-if="!previewMapIsEmpty()">
                    <p class="column-name column-preview" v-if="previewMap[key] !== 'VAZIO'" > {{ previewMap[key] }} </p>
                    <sc-field v-else :prop="`${key}_PREVIEW`">
                      <p class="column-name preview-empty"> Coluna vazia </p>
                    </sc-field>
                  </div>
                </div>
              </div>
            </section>

          </div>
          <div class="upload-form__step" v-if="previewMapIsEmpty()">
            <new-billing-template-image-helper />
          </div>
        </section>
      </template>
      <template slot="actions" slot-scope="{ isValid }">
        <section class="continue-buttom">
          <button class="button" type="button" @click="$emit('onPrevStep');">Voltar</button>
          <button class="button is-primary" type="submit">{{ buttonText }}</button>
        </section>
      </template>
    </sc-form>
  </section>
</template>

<script>
import { ScField, ScRadio, ScInput, ScForm, ScTag } from '@intelipost-js/secure-components';
import NewBillingTemplateImageHelper from './NewBillingTemplateImageHelper.vue';
import BillingTemplateColumnsEnum from '../../../enum/billing-tamplate-columns.enum';

export default {
  name: 'billingTemplateStep3',
  props: {
    previewMap: {
      type: Object,
    },
    previewMapIsEmpty: {
      type: Function,
    },
  },
  data() {
    return {
      formContent: Object.fromEntries(Object.keys(BillingTemplateColumnsEnum).map(key => [key, { type: 'ABSOLUTE' }])),
    };
  },
  components: {
    ScField, ScRadio, ScInput, ScForm, ScTag, NewBillingTemplateImageHelper,
  },
  computed: {
    buttonText() {
      return this.previewMapIsEmpty() ? 'Continuar' : 'Salvar modelo';
    },
    columnsEnum() {
      return Object.entries(BillingTemplateColumnsEnum) || {};
    },
    requiredFieldsStrict() {
      return Object.entries(BillingTemplateColumnsEnum)
        .filter(([, v]) => v.required === 1).map(([key]) => key);
    },
    requiredFieldPaired() {
      return Object.entries(BillingTemplateColumnsEnum)
        .filter(([, v]) => v.required === 0 && v.paired === true).map(([key]) => key);
    },
    requiredFields() {
      return Object.entries(BillingTemplateColumnsEnum)
        .filter(([, v]) => v.required === 0 && !!v.paired === false).map(([key]) => key);
    },
    rules() {
      return this.createRules();
    },
  },
  methods: {
    createRules() {
      return Object.fromEntries(
        this.columnsEnum.map(([key]) => [
          this.previewMapIsEmpty() ? key : `${key}_PREVIEW`,
          [{ trigger: 'submit', validator: this.requiredFieldsValidator }]],
        ),
      );
    },
    requiredFieldsValidator(rule, value, callback, fields) {
      if (rule.field.indexOf('_PREVIEW') !== -1) {
        this.previewFieldValidator(rule, value, callback);
        return;
      }

      const required = this.requiredFields;
      const requiredStrict = this.requiredFieldsStrict;
      const pairedFields = this.requiredFieldPaired;
      const errors = [];

      Object.entries(fields)
        .filter(k => k[0] !== rule.field)
        .forEach((k) => {
          if (k[1].letter === value.letter && !!value.letter) {
            errors.push(new Error('Não repetir a coluna para dados diferentes'));
          }
        });

      if (requiredStrict.includes(rule.field)) {
        if (!value.letter) {
          errors.push(new Error('Esse campo é obrigatório'));
        }
      } else if (pairedFields.includes(rule.field) || required.includes(rule.field)) {
        const isPairFilled = Object.entries(fields)
                .filter(([key]) => pairedFields.includes(key))
                .every(v => !!v[1].letter);

        const isAtLeastOneRequiredFilled = Object.entries(fields)
                            .some(([key, v]) => required.includes(key) && !!v.letter);

        if (!isAtLeastOneRequiredFilled && !isPairFilled) {
          if (pairedFields.includes(rule.field) && !isPairFilled) {
            errors.push(new Error('O número e a serie da nota devem ser preenchidos juntos'));
          } else if (required.includes(rule.field) && !isAtLeastOneRequiredFilled) {
            errors.push(new Error('Preencha ao menos um dos campos identificados para continuar'));
          }
        }
      }

      callback(errors);
    },
    previewFieldValidator(rule, value, callback) {
      const fieldName = rule.field.replace('_PREVIEW', '');
      const requiredStrict = this.requiredFieldsStrict;
      const required = this.requiredFields;
      const errors = [];

      if (requiredStrict.includes(fieldName) && this.previewMap[fieldName] === 'VAZIO') {
        errors.push(new Error('Campo obrigatório. Volte ao passo anterior e indique uma coluna válida'));
      } else if (required.includes(fieldName)) {
        const isAtLeastOneFilled = Object.entries(this.previewMap).some(([key, v]) => required.includes(key) && v !== 'VAZIO');
        if (!isAtLeastOneFilled) {
          errors.push(new Error('Campo obrigatório. Volte ao passo anterior e indique uma coluna válida'));
        }
      }

      callback(errors);
    },
    submitNextStep(form) {
      this.$emit('onNextStep', { columns: form });
    },
  },
};

</script>

<style lang="scss">
#billing-template-step-3-fields {
  .field {
    display: flex;
    align-items: center;
  }
  .tax-field {
    display: inline-flex !important;
    justify-content: space-between !important;
    max-width: 200px;
    padding-right: 0.75rem;
  }
  .percentage-field,
  .absolute-field {
    display: inline-flex;
  }

  .column-value-config {
    max-width: 200px;
  }
  .column-letter-config {
    max-width: 90px;
  }

  .column-name {
    width: 180px;
    border-radius: 10px;
    color: #010906;
    background-color: #F3F4F4;
    border: 1px solid #EBEBEB;
    padding: 8px 16px 8px 16px;
  }
  .column-type {
    width: 180px;
    padding: 8px 0px;
    text-wrap: nowrap;
  }
  .radio-label {
    margin-right: 4px;
  }

  .preview-empty {
    border: 1px solid #e84c3d;
    color: #e84c3d;
  }

  .letter-input input {
    height: 100%;
    border-radius: 10px;
    color: #010906;
    border: 1px solid #EBEBEB;

    &[disabled] {
      background-color: unset;
    }
  }

  .column-header {
    color: #39554A;
    padding: 0 !important;
  }

  .help {
    font-size: 12px;
    margin-left: 10px;
    max-width: 200px;

    &.preview-message {
      max-width: 800px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// node_modules/@intelipost-modules/secure-recon/features/billing/pages/templates/components/NewBillingTemplateStep3.vue?00ae07a1